import { useSEO } from "./seo";

export function LegalPage({ id, title, children }) {
  useSEO({
    title: `${title} — nailtape™`,
    description:
      "How nailtape™ collects, uses and protects your information — and the rights you have over it.",
    path: id === "privacy-page" ? "/privacy" : "/terms",
  });
  return (
    <div className="min-h-screen bg-bone text-ink" data-testid={id}>
      <div className="mx-auto max-w-3xl px-6 py-16 md:py-24">
        <a
          href="/"
          data-testid="legal-back-link"
          className="font-display text-sm font-bold uppercase tracking-tight text-stone-600 transition-colors hover:text-ink"
        >
          ← Back to nailtape™
        </a>
        <h1 className="mt-10 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
          {title}
        </h1>
        <p className="mt-3 font-sans text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500">
          Last updated September 2026
        </p>
        <div className="mt-10 space-y-8 border-t-2 border-ink pt-10 text-[15px] leading-relaxed text-ink/80">
          {children}
        </div>
        <p className="mt-14 border-t-2 border-ink pt-6 text-sm text-stone-500">
          Questions? Email{" "}
          <a
            href="mailto:support@nailtape.us"
            className="font-bold text-ink underline decoration-2 underline-offset-4"
          >
            support@nailtape.us
          </a>
        </p>
      </div>
    </div>
  );
}

function H({ children }) {
  return (
    <h2 className="font-display text-lg font-extrabold tracking-tight text-ink">
      {children}
    </h2>
  );
}

export function Privacy() {
  return (
    <LegalPage id="privacy-page" title="Privacy Policy">
      <p>
        This policy covers nailtape™ — the mobile app and our related services
        ("Services"). It explains what we collect, why, and the choices you
        have. Questions? <strong>support@nailtape.us</strong>.
      </p>

      <div>
        <H>What we collect</H>
        <p className="mt-3">
          <strong>From you:</strong> name, email, business name (for Pros),
          and your account type (Pro or Client).
        </p>
        <p className="mt-3">
          <strong>From platforms you connect:</strong> when you authorize a
          connection, we read order and product data from Etsy through their
          API — order numbers, customer names and emails, shipping addresses,
          items, statuses and dates — plus your shop name. Access is read-only,
          limited to the scopes you approve, and you can disconnect at any
          time. OAuth tokens are stored encrypted.
        </p>
        <p className="mt-3">
          <strong>Measurements & images:</strong> the nail images you capture
          or submit, used to process your measurements — never to identify
          you — plus the resulting numeric measurements, when they were
          submitted, and which Pro they're assigned to.
        </p>
        <p className="mt-3">
          <strong>App activity:</strong> subscription status, account
          timestamps, activity logs, push notification tokens, and aggregated,
          anonymized usage analytics.
        </p>
        <p className="mt-3">
          <strong>What we never collect:</strong> precise location, payment
          details (Apple handles billing), government IDs, or health and other
          sensitive categories. We don't buy data from brokers.
        </p>
      </div>

      <div>
        <H>How we use it</H>
        <p className="mt-3">
          To run the service — syncing orders, handling measurements, AR
          sizing and analytics; to keep accounts secure (email verification,
          fraud prevention); to let Pros and Clients coordinate orders and
          measurements; to meet legal obligations; and to improve the service
          using anonymized data.
        </p>
      </div>

      <div>
        <H>Legal bases</H>
        <p className="mt-3">
          For EU/UK users (GDPR): consent, contract performance, legitimate
          interests (security, service improvement), and legal obligations.
          Consent can be withdrawn at any time. US and Canada: consent,
          contract, and applicable law.
        </p>
      </div>

      <div>
        <H>Who we share with</H>
        <p className="mt-3">
          <strong>Google (Firebase):</strong> our database, authentication,
          cloud functions and hosting — encrypted in transit and at rest, with
          a data processing agreement in place; data is primarily stored in
          the United States.
        </p>
        <p className="mt-3">
          <strong>Apple:</strong> processes App Store billing directly — we
          see only your subscription status, never payment details.
        </p>
        <p className="mt-3">
          <strong>Etsy:</strong> the platform you connect — read-only access
          you authorize; Etsy remains the controller of its own data.
        </p>
        <p className="mt-3">
          We may also disclose information for legal requirements, business
          transfers (you'll be notified of ownership changes), or with your
          consent. <strong>We never</strong> sell your data, share it for
          third-party marketing, or use it for behavioral advertising.
        </p>
      </div>

      <div>
        <H>How long we keep it</H>
        <p className="mt-3">
          Active accounts: while your account is live. Deleted accounts:
          everything is removed within 30 days (including backups). Security
          logs: 90 days. Audit logs: 1 year. Aggregated, anonymous data may be
          kept — it can't identify you.
        </p>
      </div>

      <div>
        <H>How we protect it</H>
        <p className="mt-3">
          Encryption in transit (TLS 1.3) and at rest (AES-256), strict
          data-isolation rules enforced on every request, least-privilege
          access with multi-factor authentication, continuous monitoring, and
          a 72-hour breach-notification commitment. No system is perfect — if
          a breach ever happens, we'll notify you as the law requires.
        </p>
      </div>

      <div>
        <H>Minors</H>
        <p className="mt-3">
          nailtape™ isn't for children under 13, and we don't knowingly
          collect their data. If we learn we have, the account is deactivated
          and the data deleted within 30 days. Under 18? Get a parent's
          permission.
        </p>
      </div>

      <div>
        <H>Your rights</H>
        <p className="mt-3">
          Depending on where you live, you can access, correct, delete or
          export your data, withdraw consent, and object to processing. Manage
          everything in app settings, disconnect platform access at any time,
          or email <strong>support@nailtape.us</strong> — we respond within
          one month (GDPR) or 45 days (US states), free of charge. EU/UK data
          is transferred to the US under Google's EU–US Data Privacy Framework
          certification and Standard Contractual Clauses, and you can complain
          to your local supervisory authority.
        </p>
      </div>

      <div>
        <H>Updates</H>
        <p className="mt-3">
          We update this policy as needed — the "Last updated" date at the top
          will always show the current version, and material changes get a
          prominent notice.
        </p>
      </div>

      <div>
        <H>Contact</H>
        <p className="mt-3">
          Support: support@nailtape.us · Business/security:
          business@nailtape.us
        </p>
        <p className="mt-3">
          nailtape™, 418 Broadway Ste N, Albany, NY 12207, United States ·
          nailtape.us
        </p>
        <p className="mt-3">
          Platform policies: firebase.google.com/support/privacy ·
          apple.com/legal/privacy · etsy.com/legal/privacy
        </p>
      </div>
    </LegalPage>
  );
}

export function Terms() {
  return (
    <LegalPage id="terms-page" title="Terms of Use">
      <div>
        <H>The site</H>
        <p className="mt-3">
          This website presents nailtape™ and collects update-list signups.
          Content here is provided in good faith and may change as the product
          evolves.
        </p>
      </div>
      <div>
        <H>The app & billing</H>
        <p className="mt-3">
          nailtape™ is distributed through the Apple App Store. Subscriptions,
          free trials, cancellations and refunds are handled by Apple under
          their terms. Details are in the app's FAQ.
        </p>
      </div>
      <div>
        <H>Acceptable use</H>
        <p className="mt-3">
          Don't misuse the site or the signup system — no scraping, spamming or
          interfering with how it works for others.
        </p>
      </div>
      <div>
        <H>Ownership</H>
        <p className="mt-3">
          nailtape™, the Checkflow System™, Moodbox™ and the site's design and
          copy belong to nailtape™. Don't reproduce them without permission.
        </p>
      </div>
      <div>
        <H>Liability & changes</H>
        <p className="mt-3">
          The site is provided "as is," without warranties to the extent
          permitted by law. These terms may be updated from time to time — the
          current version always lives on this page, and continued use means
          you accept it.
        </p>
      </div>
    </LegalPage>
  );
}
